import type { BatchArgs, BatchResponse } from '@carboncopyco/types';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query/react';
import type { UseLazyQuery } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import type { QueryDefinition } from '@reduxjs/toolkit/query';
import type { BaseQueryFn } from '@reduxjs/toolkit/query/react';
import { useCallback, useState } from 'react';

type ScrollState<T> = {
  items: BatchResponse<T>['items'] | null;
  nextBatchCursor: BatchArgs['nextBatchCursor'];
};

const initialScrollState: ScrollState<any> = {
  items: null,
  nextBatchCursor: null,
};

export const useInfiniteScroll = <T extends Record<string, unknown>>(
  batchQuery: UseLazyQuery<
    QueryDefinition<
      any,
      BaseQueryFn<
        any,
        unknown,
        FetchBaseQueryError,
        Record<string, any>,
        Record<string, any>
      >,
      any,
      BatchResponse<T>
    >
  >,
  options?: {
    keyName?: 'string';
    preferCacheValue?: boolean;
    refetchOnFocus?: boolean;
  }
) => {
  const [scrollState, setScrollState] =
    useState<ScrollState<T>>(initialScrollState);
  const [trigger, { data, isLoading, error, isFetching }] = batchQuery({
    refetchOnFocus: options?.refetchOnFocus,
  });
  const [isLoadingFirstBatch, setIsLoadingFirstBatch] = useState(false);
  const preferCacheValue = options?.preferCacheValue !== false;

  const fetchFirstBatch = useCallback(
    (endpointArgs?: any) => {
      setIsLoadingFirstBatch(true);
      trigger({ ...endpointArgs, nextBatchCursor: null }, preferCacheValue)
        .unwrap()
        .then((data) => {
          setIsLoadingFirstBatch(false);
          setScrollState({
            items: data.items,
            nextBatchCursor: data.metadata.nextCursor,
          });
        })
        .catch((error) => {
          console.error(error);
        });
    },
    [setIsLoadingFirstBatch, trigger, preferCacheValue, setScrollState]
  );

  const fetchNextBatch = (endpointArgs?: any) => {
    if (!isFetching && scrollState.items && scrollState.nextBatchCursor) {
      trigger(
        {
          nextBatchCursor: scrollState.nextBatchCursor,
          ...endpointArgs,
        },
        preferCacheValue
      )
        .unwrap()
        .then((data) => {
          setScrollState({
            items: [...(scrollState.items as T[]), ...data.items],
            nextBatchCursor: data.metadata.nextCursor,
          });
        })
        .catch((error) => {
          console.error(error);
          // notify.error("Error fetching items")
        });
    }
  };

  const removeItem = (id: string) => {
    if (scrollState.items) {
      setScrollState({
        ...scrollState,
        items: scrollState.items.filter(
          (item) => String(item[options?.keyName || 'id']) !== id
        ),
      });
    }
  };

  const replaceItem = (id: T['id'], newItem: T) => {
    const itemIndex = scrollState.items.findIndex((item) => item.id === id);
    setScrollState({
      ...scrollState,
      items: scrollState.items.toSpliced(itemIndex, 1, newItem),
    });
  };

  const addItem = (item: T) => {
    setScrollState({
      ...scrollState,
      items: [item, ...(scrollState.items || [])],
    });
  };

  return {
    items: scrollState.items,
    totalSize: data?.metadata.totalItems,
    error,
    isLoading: isLoading || isLoadingFirstBatch,
    isLoadingBatch: isFetching && !isLoading && !isLoadingFirstBatch,
    fetchNextBatch,
    fetchFirstBatch,
    removeItem,
    replaceItem,
    addItem,
    isEmpty: !!(scrollState.items && scrollState.items.length === 0),
    nextBatchCursor: scrollState.nextBatchCursor,
  };
};

export type InfiniteScrollResponse<T extends Record<string, unknown>> =
  ReturnType<typeof useInfiniteScroll<T>>;
